import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useState } from "react";
import { twJoin } from "tailwind-merge";

import type {
  CommentCreateRequest,
  CommentDto,
  CommentDtoPaginationResultDto,
  CommentUpdateRequest,
  MessageReactionDto,
} from "@/api/types";
import { getCommunityMessageStatus } from "@/components/CommunityFeed/helpers";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { LastActivityLabel } from "@/components/Label/LastActivityLabel";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { getTotalReactionAmount } from "@/helpers/reactions";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

import { Lock01Icon } from "../Icons/Lock01Icon";
import { Mail01Icon } from "../Icons/Mail01Icon";
import type { CreateCommentPayload, EditCommentPayload } from "./Comment";
import { CommentList } from "./CommentList";
import { CommentReactionListModal } from "./CommentReactionListModal";
import { CommunityMessageReactCountButton, CommunityPostCommentButton } from "./CommunityMessageActivityButtons";
import { CommunityMessageReactionButton } from "./CommunityMessageReactionButton";
import { InformAdminModal } from "./InformAdminModal";
import { ReactionList } from "./ReactionList";
import type { CommunityMessageActivity } from "./types";
import { CommunityPostStatus } from "./types";

export enum CommunityPostActivityTabs {
  REACTIONS,
  COMMENTS,
}

interface CommunityMessageActivitySectionProps {
  /** Message-compatible activity data used by comments and reactions. */
  message: CommunityMessageActivity;
  /** Poll whose detail and feed caches should be refreshed after activity changes. */
  pollId?: string;
  /** Whether the full comment or reaction panel is rendered. */
  isExpanded?: boolean;
  /** Whether the current user may add comments or reactions. */
  isActionable?: boolean;
}

/** Renders a community message's reactions, comments, sharing, and translation activity controls. */
export function CommunityMessageActivitySection({
  message,
  pollId,
  isExpanded,
  isActionable = true,
}: CommunityMessageActivitySectionProps): React.ReactNode {
  const [activeTab, setActiveTab] = useState<CommunityPostActivityTabs | undefined>(
    isExpanded && (message.canComment || message.totalCommentCount > 0)
      ? CommunityPostActivityTabs.COMMENTS
      : undefined,
  );
  const [viewCommentReactionsState, setViewCommentReactionsState] = useState<{
    open: boolean;
    commentId?: string;
    likeCount?: number;
  }>({
    open: false,
  });

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const hasPermission = usePermission();
  const [isInformModalOpened, informModalHandler] = useBool(false);
  const createQuickReplyLink = communityFeedMutations.useGetQuickReplyLink();

  const communityFeedQueries = useCommunityFeedQueries();

  const { refetch: refetchComments, isFetched: isFetcheComments } = useInfiniteQuery({
    ...communityFeedQueries.commentsInfinite(message.id),
    enabled: activeTab === CommunityPostActivityTabs.COMMENTS,
  });

  const addCommentMutation = communityFeedMutations.useAddComment();
  const deleteCommentMutation = communityFeedMutations.useDeleteComment();
  const updateCommentMutation = communityFeedMutations.useUpdateComment();
  const updateMessageReactionMutation = communityFeedMutations.useUpdateMessageReaction();

  const { signalRConnection } = useSignalRHub("community-feed-detail-hub", {
    query: `userId=${sessionUser.id}&entityId=${message.id}&entityType=Message`,
    disabled: activeTab !== CommunityPostActivityTabs.COMMENTS,
  });
  const onNewLikesOrCommentsOnPost = useCallback(
    (
      ...args: [
        {
          entityId: string;
          entityType: "message" | "comment" | "poll" | "survey";
          likeTotal: number;
          commentTotal: number;
          authorId: string;
        },
      ]
    ) => {
      if (args[0].authorId === sessionUser.id) {
        return;
      }

      if (pollId) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POLL_DETAILS(projectId, pollId) });
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES_FEED(projectId) });
      }

      if (!isExpanded) {
        return;
      }

      void queryClient.setQueryData<ApiResponseType<"getMessagesDetailsV2"> | undefined>(
        QUERY_KEYS.MESSAGE_DETAILS(projectId, message.id),
        (oldData) => {
          if (!oldData) {
            return;
          }

          return {
            ...oldData,
            totalLikeCount: args[0].likeTotal,
            totalCommentCount: args[0].commentTotal,
          };
        },
      );
    },
    [isExpanded, sessionUser, queryClient, message.id, pollId, projectId],
  );
  const onNewCommentLike = useCallback(
    (...args: [{ entityId: string; likeTotal: number; authorId: string }]) => {
      if (!isExpanded || args[0].authorId === sessionUser.id) {
        return;
      }

      void queryClient.setQueryData<InfiniteData<CommentDtoPaginationResultDto> | undefined>(
        QUERY_KEYS.MESSAGE_COMMENTS(projectId, message.id),
        (oldData) => {
          if (!oldData) {
            return;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              const newItems = page.items.map((item: CommentDto) =>
                item.id === args[0].entityId ? { ...item, totalLikesCount: args[0].likeTotal } : item,
              );

              return {
                ...page,
                items: newItems,
              };
            }),
          };
        },
      );
    },
    [isExpanded, sessionUser, queryClient, message.id, projectId],
  );

  useSignalRSubscription(
    signalRConnection,
    SIGNALR_EVENTS.COMMUNITY_FEED.UPDATE_COMMENT_REACTION_COUNT,
    onNewCommentLike,
  );
  useSignalRSubscription(
    signalRConnection,
    SIGNALR_EVENTS.COMMUNITY_FEED.UPDATE_POST_REACTION_COUNT_AND_COMMENT,
    onNewLikesOrCommentsOnPost,
  );

  const handleChangeTab = (selectedTab: CommunityPostActivityTabs) => {
    switch (selectedTab) {
      case activeTab:
        setActiveTab(undefined);
        break;
      case CommunityPostActivityTabs.REACTIONS:
        setActiveTab(CommunityPostActivityTabs.REACTIONS);
        break;
      case CommunityPostActivityTabs.COMMENTS:
        setActiveTab((tab) =>
          tab === CommunityPostActivityTabs.COMMENTS ? undefined : CommunityPostActivityTabs.COMMENTS,
        );
        break;
      default:
        setActiveTab(undefined);
        break;
    }
  };

  const handleNewReaction = (reactionType: MessageReactionDto["reactionType"] | undefined) => {
    void updateMessageReactionMutation.mutateAsync({
      messageId: message.id,
      pollId,
      data: {
        reactionType,
      },
    });
  };

  if (isExpanded && !isFetcheComments) {
    void refetchComments();
  }

  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo } = useUploadVideo({});
  const handleAddComment = async ({
    comment,
    contentTemplate,
    mentions,
    images,
    videos,
    failureMessage,
    parentId,
  }: CreateCommentPayload) => {
    const payload: CommentCreateRequest = {
      content: comment,
      contentTemplate,
      mentions,
      parentId,
      videoIds: [],
    };

    if (images.length > 0) {
      const uploadedImage = await uploadFormImage(images[0]);

      payload.imageId = uploadedImage?.id;
    }

    const uploadedVideoIds: string[] = [];
    if (videos.length > 0) {
      for (const video of videos) {
        const uploadedVideo = await uploadFormVideo(video);

        if (uploadedVideo) {
          uploadedVideoIds.push(uploadedVideo.id);
        }
      }

      payload.videoIds = uploadedVideoIds;
    }

    await addCommentMutation.mutateAsync({
      messageId: message.id,
      pollId,
      payload,
      failureMessage,
    });
  };

  const handleDeleteComment = (commentId: string) => {
    return deleteCommentMutation.mutateAsync({
      messageId: message.id,
      pollId,
      commentId,
    });
  };

  const handleEditPost = async ({
    comment,
    contentTemplate,
    mentions,
    commentId,
    images,
    videos,
    failureMessage,
  }: EditCommentPayload) => {
    const payload: CommentUpdateRequest = {
      content: comment,
      contentTemplate,
      mentions,
    };

    if (images.length > 0) {
      const uploadedImage = await uploadFormImage(images[0]);

      payload.imageId = uploadedImage?.id;
    }

    const uploadedVideoIds: string[] = [];
    if (videos.length > 0) {
      for (const video of videos) {
        const uploadedVideo = await uploadFormVideo(video);

        if (uploadedVideo) {
          uploadedVideoIds.push(uploadedVideo.id);
        }
      }

      payload.videoIds = uploadedVideoIds;
    }

    return await updateCommentMutation.mutateAsync({
      messageId: message.id,
      payload,
      commentId,
      failureMessage,
    });
  };

  const hasAnyComment = message.totalCommentCount > 0;
  const hasAnyReaction = getTotalReactionAmount(message.totalReactionCountByType) > 0;
  const status = getCommunityMessageStatus(message);
  const canBeShared = isActionable && status !== CommunityPostStatus.DELETED;

  const extraShareItems: ContextMenuItem[] = [
    {
      labels: { default: t("component.community-post.actions.quick-reply-link") },
      value: "copy",
      icon: <Lock01Icon />,
      "data-testid": "post-context-menu-copy",
      callbackFn: () => void createQuickReplyLink.mutateAsync(message.id),
      isHidden: !hasPermission((x) => x.isSuperAdmin),
    },
    {
      labels: { default: t("component.community-post.actions.inform-admins") },
      value: "inform",
      icon: <Mail01Icon />,
      "data-testid": "post-context-menu-inform",
      callbackFn: informModalHandler.toggle,
      isHidden: !message.canShare,
    },
  ];

  return (
    <>
      <section
        className={twJoin(
          "flex flex-col-reverse items-start justify-between gap-space-md @md:flex-row @md:items-center",
          activeTab !== undefined && "border-b border-b-border-secondary pb-space-xs",
        )}
      >
        <nav className="flex w-full flex-wrap items-center gap-space-sm @sm:w-auto">
          <CommunityMessageReactionButton
            size="md"
            // Reactions require message permission and an active post.
            isDisabled={
              !message.canLike || status === CommunityPostStatus.ARCHIVED || status === CommunityPostStatus.DELETED
            }
            reaction={message.requesterReactionType}
            onClickReaction={handleNewReaction}
          />
          {getTotalReactionAmount(message.totalReactionCountByType) > 0 && (
            <CommunityMessageReactCountButton
              size="md"
              counts={message.totalReactionCountByType}
              isActive={activeTab === CommunityPostActivityTabs.REACTIONS}
              onClick={() => {
                handleChangeTab(CommunityPostActivityTabs.REACTIONS);
              }}
            />
          )}
          {message.type !== "announcementReadOnly" && (
            <CommunityPostCommentButton
              count={message.totalCommentCount}
              isDisabled={message.totalCommentCount === 0 && !message.canComment}
              isActive={activeTab === CommunityPostActivityTabs.COMMENTS}
              onClick={() => {
                if (message.canComment || message.totalCommentCount > 0) {
                  handleChangeTab(CommunityPostActivityTabs.COMMENTS);
                }
              }}
            />
          )}
          {/* Mobile - Share Button */}
          {canBeShared && (
            <aside className="ml-auto @sm:hidden">
              <ShareButton
                entityId={pollId ?? message.id}
                entityType={pollId ? "poll" : "message"}
                variant="ghostPrimary"
                extraItems={extraShareItems}
              />
            </aside>
          )}
        </nav>
        {(message.lastActivityAt || canBeShared) && (
          <aside className="ml-auto flex items-center gap-space-md">
            {message.lastActivityAt && <LastActivityLabel lastActivityAt={message.lastActivityAt} />}
            {/* Desktop - Share Button */}
            {canBeShared && (
              <span className="hidden @sm:flex">
                <ShareButton
                  entityId={pollId ?? message.id}
                  entityType={pollId ? "poll" : "message"}
                  variant="ghostPrimary"
                  extraItems={extraShareItems}
                />
              </span>
            )}
          </aside>
        )}
      </section>
      {activeTab === CommunityPostActivityTabs.REACTIONS && hasAnyReaction && <ReactionList messageId={message.id} />}
      {activeTab === CommunityPostActivityTabs.COMMENTS && (hasAnyComment || message.canComment) && (
        <CommentList
          message={message}
          commentPost={handleAddComment}
          editComment={handleEditPost}
          deleteComment={handleDeleteComment}
          onViewCommentReactions={(commentId, amountReactions) =>
            setViewCommentReactionsState({ open: true, commentId: commentId, likeCount: amountReactions })
          }
        />
      )}
      <CommentReactionListModal
        isOpened={viewCommentReactionsState.open}
        messageId={message.id}
        onOpenChange={(state) => {
          setViewCommentReactionsState((currState) => ({ ...currState, open: state }));
        }}
        commentId={viewCommentReactionsState.commentId}
        initialLikes={viewCommentReactionsState.likeCount}
      />
      <InformAdminModal isOpened={isInformModalOpened} onOpenChange={informModalHandler.set} messageId={message.id} />
    </>
  );
}
